import { useState, type DragEvent } from "react";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import type { BuilderDraft, FieldWidget } from "@froppio/form-core";
import type { BuilderDragSource, BuilderDropTarget } from "../types/builderDragTypes";
import {
  createField, createSection, deleteElement, elementId, findContainer, findElement,
  insertElement, moveElement, moveElementBy, updateField, updateSection,
} from "../utils/builderOperations";
import { BuilderCanvas } from "./BuilderCanvas";
import { BuilderPalette } from "./BuilderPalette";
import { BuilderSettings } from "./BuilderSettings";

interface FormBuilderProps {
  draft: BuilderDraft;
  onChange: (draft: BuilderDraft) => void;
}

export function FormBuilder({ draft, onChange }: FormBuilderProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragSource, setDragSource] = useState<BuilderDragSource | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [announcement, setAnnouncement] = useState("");

  function apply(operation: () => BuilderDraft, message: string) {
    try {
      onChange(operation());
      setError("");
      setAnnouncement(message);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to change this form.");
    }
  }

  function add(widget: FieldWidget | "section", target?: BuilderDropTarget) {
    const selected = selectedId ? findElement(draft, selectedId) : undefined;
    const sectionId = widget === "section" ? null
      : selected?.type === "section" ? selected.id : selectedId ? findContainer(draft, selectedId)?.id ?? null : null;
    const section = sectionId ? findElement(draft, sectionId) : undefined;
    const destination = target ?? { sectionId, index: section?.type === "section" ? section.elements.length : draft.elements.length };
    const field = widget === "section" ? undefined : createField(widget);
    const element = field ? { type: "field" as const, fieldId: field.id } : createSection();
    apply(() => insertElement(field ? { ...draft, fields: { ...draft.fields, [field.id]: field } } : draft, element, destination.sectionId, destination.index), "Content added.");
    setSelectedId(elementId(element));
  }

  function startDrag(event: DragEvent<HTMLElement>, source: BuilderDragSource) {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/x-froppio-form-builder", source.type === "element" ? source.id : source.widget);
    setDragSource(source);
  }

  function drop(target: BuilderDropTarget) {
    if (!dragSource) return;
    if (dragSource.type === "palette") add(dragSource.widget, target);
    else {
      apply(() => moveElement(draft, dragSource.id, target.sectionId, target.index), "Content moved.");
      setSelectedId(dragSource.id);
    }
    setDragSource(null);
  }

  function remove(id: string) {
    apply(() => deleteElement(draft, id), "Content deleted.");
    setSelectedId(null);
    setDeleteId(null);
  }

  return (
    <>
      {error && <ErrorState message={error} />}
      <p role="status" className="sr-only">{announcement}</p>
      <div className="grid min-w-0 grid-cols-[200px_minmax(0,1fr)_260px] items-start gap-5 xl:grid-cols-[220px_minmax(0,1fr)_280px]">
        <BuilderPalette onAdd={add} onDragStart={startDrag} onDragEnd={() => setDragSource(null)} />
        <BuilderCanvas draft={draft} selectedId={selectedId} dragSource={dragSource} onSelect={setSelectedId}
          onMove={(id, direction) => { apply(() => moveElementBy(draft, id, direction), "Content moved."); setSelectedId(id); }}
          onDragStart={startDrag} onDragEnd={() => setDragSource(null)} onDrop={drop} />
        <BuilderSettings draft={draft} selectedId={selectedId}
          onFieldChange={field => apply(() => updateField(draft, field), "Question updated.")}
          onSectionChange={section => apply(() => updateSection(draft, section), "Section updated.")}
          onChangeContainer={(id, sectionId) => {
            const section = sectionId ? findElement(draft, sectionId) : undefined;
            apply(() => moveElement(draft, id, sectionId, section?.type === "section" ? section.elements.length : draft.elements.length), "Question moved.");
          }}
          onDelete={id => {
            const element = findElement(draft, id);
            if (element?.type === "section" && element.elements.length) setDeleteId(id);
            else remove(id);
          }} />
      </div>
      <ConfirmationDialog open={deleteId !== null} onOpenChange={open => { if (!open) setDeleteId(null); }}
        title="Delete this section and its questions?" description="All questions in this section will be removed from your draft. This cannot be undone. The saved version is unchanged until you save."
        confirmLabel="Delete section and questions" dismissLabel="Keep section" destructive onConfirm={() => { if (deleteId) remove(deleteId); }} />
    </>
  );
}
